import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import { apiGet, apiSend, apiFetch, errorMessage } from "./api";
import "./UserManagement.css";

function UserManagement({ user, onNavigate, onLogout }) {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [revokingOrgId, setRevokingOrgId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchUsers = useCallback(async (signal) => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 20 });
    if (search) params.append("search", search);
    if (roleFilter) params.append("role", roleFilter);
    if (statusFilter) params.append("status", statusFilter);

    try {
      const data = await apiGet(`/api/users?${params}`, "Impossible de charger les utilisateurs");
      if (signal?.aborted) return;
      setUsers(data.users);
      setTotalPages(data.pages);
      setTotal(data.total);
      setError("");
    } catch (e) {
      if (signal?.aborted) return;
      setUsers([]);
      setTotal(0);
      setTotalPages(1);
      setError(errorMessage(e, "Impossible de charger les utilisateurs"));
    }
    if (!signal?.aborted) setLoading(false);
  }, [page, search, roleFilter, statusFilter]);

  // Debounce : sans lui, une requête part à chaque frappe et les réponses
  // peuvent revenir dans le désordre.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => fetchUsers(controller.signal), 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [fetchUsers]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  // L'abonnement est porté par l'organisation : révoquer depuis n'importe quel
  // compte du commerce annule l'abonnement Stripe et remet tout le commerce
  // (gérant + employés) sans plan.
  const revokeSubscription = async (u) => {
    if (!window.confirm(
      `Révoquer l'abonnement de « ${u.organization} » ? L'abonnement Stripe ` +
      "sera annulé immédiatement et tous les comptes de ce commerce " +
      "repasseront sans plan."
    )) {
      return;
    }
    setRevokingOrgId(u.organization_id);
    try {
      await apiSend(
        `/api/billing/admin/organizations/${u.organization_id}/cancel-subscription`,
        "POST", {}, "Impossible de révoquer l'abonnement");
      setError("");
      await fetchUsers();
    } catch (e) {
      setError(errorMessage(e, "Impossible de révoquer l'abonnement"));
    }
    setRevokingOrgId(null);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(
      `Supprimer définitivement le compte de ${u.surname} ${u.name} ` +
      `(${u.email}) ? Cette action est irréversible.`
    )) {
      return;
    }
    setDeletingId(u.id);
    try {
      await apiFetch(`/api/users/${u.id}`, { method: "DELETE" },
        "Impossible de supprimer l'utilisateur");
      setError("");
      await fetchUsers();
    } catch (e) {
      setError(errorMessage(e, "Impossible de supprimer l'utilisateur"));
    }
    setDeletingId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="um-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Gestion Utilisateurs"
        onBack={() => onNavigate("admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="um-main">
        <div className="um-filters">
          <input
            type="text"
            className="um-search"
            placeholder="Rechercher (nom, prénom, email)..."
            value={search}
            onChange={handleSearch}
          />
          <select className="um-select" value={roleFilter} onChange={handleRoleFilter}>
            <option value="">Tous les rôles</option>
            <option value="Admin">Admin</option>
            <option value="User">User</option>
            <option value="Sale">Sale</option>
            <option value="Staff">Staff</option>
          </select>
          <select className="um-select" value={statusFilter} onChange={handleStatusFilter}>
            <option value="">Tous les statuts</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="disabled">Disabled</option>
            <option value="banned">Banned</option>
          </select>
        </div>

        <div className="um-toolbar">
          <p className="um-count">{total} utilisateur{total > 1 ? "s" : ""}</p>
          <button className="um-btn-create" onClick={() => setShowCreate(true)}>+ Créer un utilisateur</button>
        </div>

        {error && <div className="um-error">{error}</div>}

        {loading ? (
          <p className="um-loading">Chargement...</p>
        ) : users.length === 0 ? (
          <p className="um-empty">Aucun utilisateur trouvé</p>
        ) : (
          <>
            <div className="um-table-wrapper">
              <table className="um-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Client</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Plan</th>
                    <th>Organisation</th>
                    <th>Inscription</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>
                        <span className="um-client-name">{u.surname} {u.name}</span>
                        <span className="um-client-email" title={u.email}>{u.email}</span>
                        {u.phone && <span className="um-client-email">{u.phone}</span>}
                      </td>
                      <td><span className={`um-badge um-badge-${u.role.toLowerCase()}`}>{u.role}</span></td>
                      <td><span className={`um-badge um-badge-${u.status.toLowerCase()}`}>{u.status}</span></td>
                      <td>{u.plan && u.plan !== "None" ? u.plan : "-"}</td>
                      <td className="um-ellipsis" title={u.organization || ""}>{u.organization || "-"}</td>
                      <td>{formatDate(u.date_creation)}</td>
                      <td className="um-actions">
                        <button className="um-btn-edit" onClick={() => setEditingUser(u)}>Modifier</button>
                        {u.org_subscription_id && (
                          <button
                            className="um-btn-revoke"
                            onClick={() => revokeSubscription(u)}
                            disabled={revokingOrgId !== null}
                          >
                            {revokingOrgId === u.organization_id
                              ? "Révocation..." : "Révoquer l'abonnement"}
                          </button>
                        )}
                        {u.id !== user.id && (
                          <button
                            className="um-btn-delete"
                            onClick={() => deleteUser(u)}
                            disabled={deletingId !== null}
                          >
                            {deletingId === u.id ? "Suppression..." : "Supprimer"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards for mobile */}
            <div className="um-cards">
              {users.map((u) => (
                <div className="um-card" key={u.id}>
                  <div className="um-card-header">
                    <strong>{u.surname} {u.name}</strong>
                    <span className={`um-badge um-badge-${u.role.toLowerCase()}`}>{u.role}</span>
                  </div>
                  <div className="um-card-row">{u.email}</div>
                  {u.phone && <div className="um-card-row">{u.phone}</div>}
                  {u.organization && <div className="um-card-row">{u.organization}</div>}
                  {u.plan && u.plan !== "None" && <div className="um-card-row">Plan : {u.plan}</div>}
                  <div className="um-card-footer">
                    <span className={`um-badge um-badge-${u.status.toLowerCase()}`}>{u.status}</span>
                    <span className="um-card-date">{formatDate(u.date_creation)}</span>
                  </div>
                  <button className="um-btn-edit um-btn-edit-mobile" onClick={() => setEditingUser(u)}>Modifier</button>
                  {u.org_subscription_id && (
                    <button
                      className="um-btn-revoke um-btn-edit-mobile"
                      onClick={() => revokeSubscription(u)}
                      disabled={revokingOrgId !== null}
                    >
                      {revokingOrgId === u.organization_id
                        ? "Révocation..." : "Révoquer l'abonnement"}
                    </button>
                  )}
                  {u.id !== user.id && (
                    <button
                      className="um-btn-delete um-btn-edit-mobile"
                      onClick={() => deleteUser(u)}
                      disabled={deletingId !== null}
                    >
                      {deletingId === u.id ? "Suppression..." : "Supprimer"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {totalPages > 1 && (
          <div className="um-pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
          </div>
        )}
        {showCreate && (
          <CreateUserModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { setShowCreate(false); setPage(1); fetchUsers(); }}
          />
        )}
        {editingUser && (
          <EditUserModal
            userToEdit={editingUser}
            onClose={() => setEditingUser(null)}
            onUpdated={() => { setEditingUser(null); fetchUsers(); }}
          />
        )}
      </main>
    </div>
  );
}

export default UserManagement;
